import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { DiscussionSidebarComponent } from '../discussion-sidebar/discussion-sidebar.component';
import { DiscussionItem, DiscussionAttachment } from '../discussion-home/discussion-home.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { EditorModule } from 'primeng/editor';
import { FormatService } from '../../../shared/services/format.service';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ToasterService } from '../../../services/toaster.service';

export type CommentStatus = 'pending' | 'approved' | 'rejected';

export interface DiscussionComment {
  id: string;
  author: string;
  authorAvatar?: string;
  content: string;
  timestamp: string;
  status?: CommentStatus;
  replies?: DiscussionComment[];
  upvotes?: number;
  userUpvoted?: boolean;
  reactions?: { [key: string]: number };
  userReactions?: string[];
}

@Component({
  selector: 'app-discussion-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    DiscussionSidebarComponent,
    ModalComponent,
    EditorModule,
    LoginModalComponent
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class DiscussionDetailComponent implements OnInit {
  discussionId: string | null = null;
  discussion: DiscussionItem | null = null;
  /** Created-at from API for asset header (replaces description) */
  discussionCreatedAt: string | null = null;
  activeTab: string = 'getting-started';
  comments: DiscussionComment[] = [];
  newComment: string = '';
  isSubmitting: boolean = false;
  showCommentEditorModal: boolean = false;
  replyingToCommentId: string | null = null;
  replyContent: string = '';

  /** Per-comment state: replies section expanded (default true when not set). */
  repliesExpanded: Record<string, boolean> = {};

  // Loading and error states
  isLoading: boolean = false;
  errorMessage: string | null = null;

  /** Login status for header actions (Bookmark, Pin, Vote) */
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;

  /** Keycloak subject — compared to API `user.id` for ownership */
  loggedInUserId: string | null = null;
  isDiscussionOwner: boolean = false;
  /** Latest admin review comment from `discussion_reviews` */
  adminComment: string | null = null;

  /** Vote state for discussion (header upvote/downvote) */
  userVote: 'up' | 'down' | null = null;
  voteCount: number = 0;

  readonly COMMENT_MAX_LENGTH = 2000;

  // Editor configuration
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['code-block']
    ]
  };

  /**
   * Strip HTML on paste so only plain text is inserted (blocks pasted HTML tags).
   */
  onEditorInit(event: any): void {
    const editor = event?.editor;
    if (!editor) return;
    const clipboard = editor.getModule('clipboard');
    if (!clipboard) return;
    const originalPaste = clipboard.dangerouslyPasteHTML?.bind(clipboard);
    if (typeof originalPaste !== 'function') return;
    clipboard.dangerouslyPasteHTML = (html: string, source?: string) => {
      const plain = this.formatService.stripAllHtml(html ?? '');
      const escaped = plain
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const safeHtml = plain ? `<p>${escaped}</p>` : '<p><br></p>';
      return originalPaste(safeHtml, source);
    };
  }

  get headerConfig(): AssetHeaderConfig {
    const createdAtLabel = this.discussionCreatedAt
      ? this.formatService.formatDate(this.discussionCreatedAt)
      : '';
    return {
      breadcrumbs: [
        { label: 'Home', route: '/' },
        { label: 'Discussions', route: '/discussions' },
        { label: this.discussion?.title || 'Discussion' }
      ],
      title: this.discussion?.title || 'Discussion',
      description: '',
      createdAt: createdAtLabel || undefined,
      assetId: this.discussionId ?? undefined
    };
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formatService = inject(FormatService);
  private discussionApiService = inject(DiscussionApiService);
  private keycloak = inject(KeycloakService);
  private toasterService = inject(ToasterService);

  ngOnInit(): void {
    this.keycloak.isLoggedIn().then((loggedIn) => {
      this.isLoggedIn = loggedIn;
      if (loggedIn) {
        const sub = (this.keycloak.getKeycloakInstance() as any)?.tokenParsed
          ?.sub as string | undefined;
        this.loggedInUserId = sub ?? null;
      } else {
        this.loggedInUserId = null;
      }
    });
    this.route.paramMap.subscribe(params => {
      this.discussionId = params.get('id');
      if (this.discussionId) {
        this.loadDiscussion(this.discussionId);
      }
    });
  }

  loadDiscussion(id: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.discussionCreatedAt = null;
    this.userVote = null;
    this.voteCount = 0;
    this.adminComment = null;
    this.isDiscussionOwner = false;

    this.discussionApiService.getReview(id).then((response) => {
      const data = response?.data ?? response;
      if (!data) {
        this.errorMessage = 'Discussion not found';
        this.isLoading = false;
        this.loadComments();
        return;
      }
      const sub = (this.keycloak.getKeycloakInstance() as any)?.tokenParsed
        ?.sub as string | undefined;
      if (sub) this.loggedInUserId = sub;
      this.discussionCreatedAt = data.created_at ?? data.createdAt ?? null;
      this.discussion = this.mapApiResponseToDiscussionItem(data);
      this.adminComment = this.extractLatestAdminComment(
        data.discussion_reviews,
      );
      const ownerId = data.user?.id ?? data.user_id ?? data.owner_id;
      this.isDiscussionOwner =
        !!this.loggedInUserId &&
        !!ownerId &&
        String(this.loggedInUserId) === String(ownerId);
      this.voteCount = data.vote_count ?? data.upvotes ?? data.votes ?? 0;
      const uv = data.user_vote ?? (data.is_voted ? 'up' : null);
      this.userVote = uv === 'down' ? 'down' : uv === 'up' ? 'up' : null;
      this.isLoading = false;
      this.loadComments();
    }).catch((error) => {
      console.error('Error loading discussion:', error);
      this.discussion = null;
      this.discussionCreatedAt = null;
      this.errorMessage = 'Failed to load discussion. Please try again.';
      this.isLoading = false;
      this.loadComments();
    });
  }

  /** Toggle bookmark status */
  toggleBookmark(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.discussion || !this.discussionId) return;
    const newBookmarkState = !this.discussion.isBookmarked;
    const previousState = this.discussion.isBookmarked;
    this.discussion.isBookmarked = newBookmarkState;
    const apiCall = previousState
      ? this.discussionApiService.unbookmarkDiscussion(this.discussionId)
      : this.discussionApiService.bookmarkDiscussion(this.discussionId);
    apiCall
      .then(() => {
        this.toasterService.success(
          newBookmarkState
            ? 'This discussion has been added to your bookmarks. You can open it anytime from My Bookmarks.'
            : 'This discussion has been removed from your bookmarks.'
        );
      })
      .catch((error) => {
        console.error('Error toggling bookmark:', error);
        if (this.discussion) this.discussion.isBookmarked = previousState;
        this.toasterService.error((error as any)?.error?.detail || 'Failed to update bookmark');
      });
  }

  /** Toggle pin status */
  togglePin(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.discussion || !this.discussionId) return;
    const newPinState = !this.discussion.isPinned;
    const previousState = this.discussion.isPinned;
    this.discussion.isPinned = newPinState;
    const apiCall = previousState
      ? this.discussionApiService.unpinDiscussion(this.discussionId)
      : this.discussionApiService.pinDiscussion(this.discussionId);
    apiCall
      .then(() => {
        this.toasterService.success(
          newPinState
            ? 'This discussion is now pinned. Find it under My Pinned in Discussions.'
            : 'This discussion has been unpinned.'
        );
      })
      .catch((error) => {
        console.error('Error toggling pin:', error);
        if (this.discussion) this.discussion.isPinned = previousState;
        this.toasterService.error((error as any)?.error?.detail || 'Failed to update pin');
      });
  }

  private showVoteFeedbackToast(): void {
    if (this.userVote === 'up') {
      this.toasterService.success('You upvoted this discussion.');
    } else if (this.userVote === 'down') {
      this.toasterService.success('You downvoted this discussion.');
    } else {
      this.toasterService.success('Your vote on this discussion was removed.');
    }
  }

  /** Upvote discussion */
  toggleUpvote(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.discussionId) return;
    const previousVote = this.userVote;
    this.userVote = 'up';
    this.voteCount = this.voteCount + (previousVote === 'up' ? 0 : previousVote === 'down' ? 2 : 1);
    this.discussionApiService.upvoteDiscussion(this.discussionId)
      .then((res: any) => {
        const data = res?.data ?? res;
        if (data?.vote_count !== undefined) this.voteCount = data.vote_count;
        if (data?.user_vote !== undefined) this.userVote = data.user_vote === 'down' ? 'down' : data.user_vote === 'up' ? 'up' : null;
        this.showVoteFeedbackToast();
      })
      .catch((error) => {
        console.error('Error upvoting:', error);
        this.userVote = previousVote;
        this.voteCount = this.voteCount - (previousVote === 'up' ? 0 : previousVote === 'down' ? 2 : 1);
        this.toasterService.error((error as any)?.error?.detail || 'Failed to upvote');
      });
  }

  /** Toggle thumbs up: upvote when not upvoted, downvote when already upvoted */
  toggleThumbsUp(): void {
    if (this.userVote === 'up') {
      this.toggleDownvote();
    } else {
      this.toggleUpvote();
    }
  }

  /** Downvote discussion */
  toggleDownvote(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.discussionId) return;
    const previousVote = this.userVote;
    this.userVote = 'down';
    this.voteCount = Math.max(0, this.voteCount + (previousVote === 'down' ? 0 : previousVote === 'up' ? -2 : -1));
    this.discussionApiService.downvoteDiscussion(this.discussionId)
      .then((res: any) => {
        const data = res?.data ?? res;
        if (data?.vote_count !== undefined) this.voteCount = data.vote_count;
        if (data?.user_vote !== undefined) this.userVote = data.user_vote === 'down' ? 'down' : data.user_vote === 'up' ? 'up' : null;
        this.showVoteFeedbackToast();
      })
      .catch((error) => {
        console.error('Error downvoting:', error);
        this.userVote = previousVote;
        this.voteCount = this.voteCount + (previousVote === 'down' ? 0 : previousVote === 'up' ? 2 : 1);
        this.toasterService.error((error as any)?.error?.detail || 'Failed to downvote');
      });
  }

  /** Get status display label */
  /** Format date for Discussion Details: DD-MMM-YYYY HH:MM in IST */
  formatDetailDate(value: string | null | undefined): string {
    return value ? this.formatService.formatDate(value) : '';
  }

  getStatusLabel(status: string | undefined): string {
    if (!status) return '';
    const labels: { [key: string]: string } = {
      'APPROVED': 'Approved',
      'PENDING': 'Pending',
      'REJECTED': 'Rejected',
      'CHANGES_REQUIRED': 'Changes Required'
    };
    return labels[status] || status;
  }

  /** Navigate to edit discussion (create page with ?edit=) */
  navigateToEdit(): void {
    if (!this.discussionId) return;
    this.router.navigate(['/discussions/create-new'], {
      queryParams: { edit: this.discussionId },
    });
  }

  /** Latest non-empty admin comment from discussion_reviews (newest by created_at). */
  private extractLatestAdminComment(reviews: unknown): string | null {
    if (!Array.isArray(reviews) || reviews.length === 0) return null;
    const sorted = [...reviews].sort((a: any, b: any) => {
      const ta = new Date(a?.created_at ?? 0).getTime();
      const tb = new Date(b?.created_at ?? 0).getTime();
      return tb - ta;
    });
    const latest = sorted[0];
    const text =
      typeof latest?.comment === 'string' ? latest.comment.trim() : '';
    return text.length ? text : null;
  }

  /** Navigate to asset detail page (databank/dataset, model, usecase) in new tab */
  navigateToAsset(): void {
    if (!this.discussion?.subCategoryId || !this.discussion?.category) return;
    
    const assetTypeMap: { [key: string]: string } = {
      'DATA_BANKS': 'dataset',
      'AI_MODELS': 'model',
      'USE_CASES': 'usecase'
    };
    
    const assetType = assetTypeMap[this.discussion.category];
    if (assetType) {
      const url = this.router.serializeUrl(this.router.createUrlTree([`/${assetType}/${this.discussion.subCategoryId}`]));
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /** Display text for linked asset: name only (row label already shows type, e.g. Dataset Name). */
  getSubCategoryDisplayText(): string {
    if (!this.discussion?.category) return '—';
    const name = this.discussion.assetName?.trim();
    if (name) {
      return this.stripRedundantAssetTypePrefix(name, this.discussion.category);
    }
    const id = this.discussion.subCategoryId?.trim();
    return id || '—';
  }

  /** Remove leading "Dataset ·" / "Dataset -" when API echoes the asset type in the name. */
  private stripRedundantAssetTypePrefix(raw: string, category: string): string {
    const typeLabel = this.getCategoryDisplayLabel(category);
    if (!typeLabel) return raw;
    const escaped = typeLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^\\s*${escaped}\\s*(?:[·\\-–—:]\\s*)+`, 'i');
    const stripped = raw.replace(re, '').trim();
    return stripped || raw;
  }

  /** Label for asset row: "{category} Name" from response (e.g. Dataset Name, Model Name) */
  getSubCategoryLabel(): string {
    if (!this.discussion?.category) return 'Asset Name';
    const cat = this.getCategoryDisplayLabel(this.discussion.category);
    return `${cat} Name`;
  }

  /** Map API category (e.g. DATA_BANKS) to display label (Dataset, Model, Usecase) */
  getCategoryDisplayLabel(category: string | undefined): string {
    if (!category) return '';
    const map: { [key: string]: string } = {
      DATA_BANKS: 'Dataset',
      AI_MODELS: 'Model',
      USE_CASES: 'Usecase',
      OTHERS: 'Others'
    };
    return map[category] ?? category;
  }

  /** First attachment object */
  getFirstAttachment(): DiscussionAttachment | null {
    const first = this.discussion?.attachments?.[0];
    return first ?? null;
  }

  /** URL of first attachment for direct download (when available) */
  getFirstAttachmentUrl(): string | null {
    const first = this.discussion?.attachments?.[0];
    if (!first?.url || first.url === '#') return null;
    return first.url;
  }

  /** Name of first attachment for display */
  getFirstAttachmentName(): string {
    const first = this.discussion?.attachments?.[0];
    return first?.name ?? '';
  }

  /** Map API response to DiscussionItem for detail view */
  private mapApiResponseToDiscussionItem(data: any): DiscussionItem {
    const category = data.category || data.type || '';
    const tags: string[] = (data.discussion_tags || [])
      .map((dt: any) => dt?.tag?.name)
      .filter((n: string | undefined) => n);
    const attachments: DiscussionAttachment[] = (data.discussion_attachments || []).map((a: any) => {
      const meta = a.attachment_metadata;
      const name = meta?.file_name ?? a.file_name ?? a.name ?? a.attachment?.name ?? 'Attachment';
      const s3Key = meta?.s3_key ?? meta?.['s3 key'] ?? a.object_key ?? a.objectKey ?? undefined;
      return {
        id: a.id || a.attachment_id || '',
        name,
        url: a.url ?? a.file_url ?? a.attachment?.url ?? '#',
        type: a.type ?? meta?.content_type ?? a.mime_type ?? a.attachment?.type ?? 'application/octet-stream',
        size: a.size ?? meta?.content_length_bytes ?? meta?.size_in_kb ?? a.file_size ?? a.attachment?.size,
        s3Key: s3Key || undefined
      };
    });

    return {
      id: data.id || '',
      title: data.title || 'Untitled',
      description: data.description || data.content || '',
      content: data.content || data.description || '',
      author: data.user?.name || data.author?.name || data.author || 'Unknown',
      category,
      replies: data.reply_count ?? data.replies ?? 0,
      views: data.view_count ?? data.views ?? 0,
      lastActivity: data.updated_at ?? data.updatedAt ?? data.created_at ?? '',
      isPinned: data.is_pinned ?? false,
      isBookmarked: data.is_bookmarked ?? false,
      isVoted: data.is_voted ?? false,
      status: data.status ?? data.review_status ?? undefined,
      subCategoryId: data.sub_category_id ?? data.subCategoryId ?? undefined,
      assetType: this.getCategoryDisplayLabel(category) as 'Dataset' | 'Model' | 'Usecase' || undefined,
      assetName: data.sub_category ?? data.sub_category_name ?? undefined,
      tags: tags.length ? tags : undefined,
      attachments: attachments.length ? attachments : undefined,
      created_at: data.created_at ?? data.createdAt ?? undefined,
      updated_at: data.updated_at ?? data.updatedAt ?? undefined
    };
  }

  loadComments(): void {
    if (!this.discussionId) {
      this.comments = [];
      return;
    }
    this.discussionApiService
      .getDiscussionComment(this.discussionId, 1, 200, 'newest')
      .then((response) => {
        const rawList = this.getCommentsListFromResponse(response);
        this.comments = rawList.map((item: any) => this.mapApiCommentToDiscussionComment(item));
        // On page load, fetch replies for each comment via discussionReplies(commentId)
        this.comments.forEach((c) => this.loadRepliesForComment(c.id));
      })
      .catch(() => {
        this.comments = [];
      });
  }

  /** Extract comments array from API response */
  private getCommentsListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.comments && Array.isArray(data.comments)) return data.comments;
    return [];
  }

  /** Extract replies array from discussionReplies API response */
  private getRepliesListFromResponse(response: any): any[] {
    if (!response) return [];
    const data = response.data ?? response;
    if (Array.isArray(data)) return data;
    if (data?.replies && Array.isArray(data.replies)) return data.replies;
    return [];
  }

  /** Load replies for a single comment (e.g. after posting a reply) and update its replies. */
  loadRepliesForComment(commentId: string): void {
    const comment = this.comments.find(c => c.id === commentId);
    if (!comment) return;
    this.discussionApiService
      .discussionReplies(commentId, 1, 200)
      .then((response) => {
        const rawList = this.getRepliesListFromResponse(response);
        const replies = rawList.map((item: any) => this.mapApiCommentToDiscussionComment(item));
        comment.replies = replies.length ? replies : undefined;
      })
      .catch(() => {
        // Keep existing replies on error
      });
  }

  /** Map API comment item to DiscussionComment */
  private mapApiCommentToDiscussionComment(item: any): DiscussionComment {
    const replies: DiscussionComment[] = (item.replies || item.children || []).map((r: any) =>
      this.mapApiCommentToDiscussionComment(r)
    );
    const timestamp = item.created_at ?? item.updated_at ?? item.createdAt ?? item.updatedAt ?? '';
    const { reactions: mappedReactions, userReactions: mappedUserReactions } =
      this.mapApiReactionsToCommentReactions(item);
    return {
      id: item.id || item._id || '',
      author: item.user?.name ?? item.author?.name ?? item.author ?? 'Unknown',
      authorAvatar: item.user?.avatar ?? item.author?.avatar,
      content: item.comment ?? item.content ?? item.body ?? '',
      timestamp: timestamp ? this.formatCommentTimestamp(timestamp) : 'Unknown',
      status: this.normalizeCommentStatus(item),
      replies: replies.length ? replies : undefined,
      upvotes: item.upvotes ?? item.votes ?? item.vote_count ?? 0,
      userUpvoted: item.user_upvoted ?? item.userUpvoted ?? item.is_voted ?? false,
      reactions: mappedReactions,
      userReactions: mappedUserReactions
    };
  }

  /**
   * Map API reaction payload to UI shape.
   * API may send reactions: { emojis: { "&#10084;&#65039;": 1 }, user_reaction: { emoji_code: "&#10084;&#65039;" } }
   * or comment_reactions with same shape. UI needs reactions: { "❤️": 1 }, userReactions: ["❤️"].
   */
  private mapApiReactionsToCommentReactions(item: any): {
    reactions: { [key: string]: number };
    userReactions: string[];
  } {
    const raw =
      item.reactions ?? item.comment_reactions ?? {};
    const emojis = raw.emojis ?? raw.reactions ?? {};
    const userReaction = raw.user_reaction ?? raw.user_reactions;

    const reactions: { [key: string]: number } = {};
    if (emojis && typeof emojis === 'object' && !Array.isArray(emojis)) {
      for (const [entityKey, count] of Object.entries(emojis)) {
        const emoji = this.decimalHtmlEntityToEmoji(String(entityKey));
        if (emoji && typeof count === 'number') reactions[emoji] = count;
      }
    }

    const userReactions: string[] = [];
    if (userReaction != null) {
      const list = Array.isArray(userReaction)
        ? userReaction
        : [userReaction];
      for (const entry of list) {
        const code = entry?.emoji_code ?? entry;
        if (typeof code === 'string') {
          const emoji = this.decimalHtmlEntityToEmoji(code);
          if (emoji && !userReactions.includes(emoji)) userReactions.push(emoji);
        }
      }
    }

    if (Object.keys(reactions).length === 0 && userReactions.length === 0) {
      return {
        reactions: item.reactions && typeof item.reactions === 'object' && !item.reactions.emojis ? item.reactions : {},
        userReactions: item.user_reactions ?? item.userReactions ?? []
      };
    }
    return { reactions, userReactions };
  }

  /** Comment / reply: recent = relative phrase; older = IST datetime (FormatService.formatDiscussionActivity). */
  private formatCommentTimestamp(value: string | Date): string {
    if (value == null || value === '') return 'Unknown';
    if (typeof value === 'string') {
      const t = value.trim();
      return t ? this.formatService.formatDiscussionActivity(t) : 'Unknown';
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return this.formatService.formatDiscussionActivity(value.toISOString());
    }
    return 'Unknown';
  }

  get commentLength(): number {
    // Strip HTML tags to get actual text length
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.newComment || '';
    return tempDiv.textContent?.length || 0;
  }

  /** Section title count: top-level comments only, excluding rejected (rejected rows still render). */
  get topLevelCommentCountExcludingRejected(): number {
    return this.comments.filter((c) => c.status !== 'rejected').length;
  }

  hasContent(value: string): boolean {
    return !!(value && value.trim().length > 0);
  }

  /** Whether the comment/reply has actual text (HTML stripped). Used to enable/disable Save. */
  hasCommentTextForSave(): boolean {
    const raw = this.replyingToCommentId ? this.replyContent : this.newComment;
    if (!raw || typeof raw !== 'string') return false;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = raw;
    const text = (tempDiv.textContent || '').trim();
    return text.length > 0;
  }

  openCommentEditor(): void {
    this.replyingToCommentId = null;
    this.replyContent = '';
    this.showCommentEditorModal = true;
  }

  openReplyEditor(commentId: string): void {
    this.replyingToCommentId = commentId;
    this.replyContent = '';
    this.showCommentEditorModal = true;
  }

  saveComment(): void {
    if (this.replyingToCommentId) {
      this.saveReply();
      // Modal and state are cleared in saveReply() when the API succeeds
    } else {
      this.submitComment();
    }
  }

  cancelComment(): void {
    this.discardCommentEditorDraft();
    this.showCommentEditorModal = false;
  }

  onCommentEditorModalClose(): void {
    this.discardCommentEditorDraft();
  }

  private discardCommentEditorDraft(): void {
    this.newComment = '';
    this.replyContent = '';
    this.replyingToCommentId = null;
  }

  saveReply(): void {
    if (!this.replyingToCommentId || !this.replyContent.trim()) {
      return;
    }

    // Strip HTML to check if there's actual content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.replyContent || '';
    const textContent = tempDiv.textContent?.trim() || '';

    if (!textContent || this.isSubmitting) {
      return;
    }

    const commentId = this.replyingToCommentId;
    const replyHtml = this.replyContent.trim();

    // Optimistic UI: add reply to the comment
    const comment = this.comments.find(c => c.id === commentId);
    if (comment) {
      if (!comment.replies) {
        comment.replies = [];
      }
      comment.replies.push({
        id: '',
        author: 'You',
        content: replyHtml,
        timestamp: 'Just now',
        status: 'pending',
        upvotes: 0,
        userUpvoted: false,
        reactions: {},
        userReactions: []
      });
      if (this.discussion) {
        this.discussion.replies = (this.discussion.replies || 0) + 1;
      }
    }

    this.isSubmitting = true;
    this.discussionApiService
      .commentReply(commentId, { reply: replyHtml, attachments: [] })
      .then(() => {
        this.showCommentEditorModal = false;
        this.replyingToCommentId = null;
        this.replyContent = '';
        this.loadRepliesForComment(commentId);
      })
      .catch(() => {
        if (comment?.replies?.length) {
          comment.replies.pop();
        }
        if (this.discussion) {
          this.discussion.replies = Math.max(0, (this.discussion.replies || 1) - 1);
        }
        this.toasterService.error('Failed to post reply. Please try again.');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  getAuthorInitials(): string {
    if (!this.discussion?.author) return 'U';
    const author = this.discussion.author;
    // Handle email format (e.g., benchmark@tanuh.ai)
    const namePart = author.split('@')[0];
    // Extract first two chars or first letter of name parts
    const parts = namePart.split(/[._-]/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return namePart.substring(0, 2).toUpperCase();
  }

  getPlainTextContent(): string {
    if (!this.discussion) return '';
    const content = this.discussion.content || this.discussion.description || '';
    // Strip HTML tags using the FormatService method
    return this.formatService.stripAllHtml(content);
  }

  getAssetTypeLabel(): string {
    if (!this.discussion?.assetType) return '';
    return this.discussion.assetType;
  }

  isDownloadingAttachment = false;

  async downloadAttachment(attachment: DiscussionAttachment): Promise<void> {
    if (!attachment) return;
    if (attachment.url && attachment.url !== '#') {
      const a = document.createElement('a');
      a.href = attachment.url;
      a.download = attachment.name || 'download';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
      return;
    }
    if (!attachment.id) {
      this.toasterService.error('Download is not available for this attachment');
      return;
    }
    this.isDownloadingAttachment = true;
    try {
      const result = await this.discussionApiService.generateAttachmentDownloadUrl(attachment.id);
      const downloadUrl = result?.download_url ?? result?.presigned_url;
      if (downloadUrl) {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = attachment.name || 'download';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      } else {
        this.toasterService.error('Could not get download link');
      }
    } catch {
      this.toasterService.error('Failed to download attachment');
    } finally {
      this.isDownloadingAttachment = false;
    }
  }

  submitComment(): void {
    const commentHtml = this.newComment?.trim() || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = commentHtml;
    const textContent = tempDiv.textContent?.trim() || '';

    if (!textContent || this.isSubmitting || !this.discussionId) {
      return;
    }

    this.isSubmitting = true;

    const payload = {
      comment: commentHtml,
      attachments: [] as string[],
    };

    this.discussionApiService
      .saveComment(this.discussionId, payload)
      .then((response) => {
        this.showCommentEditorModal = false;
        this.newComment = '';
        if (this.discussion) {
          this.discussion.replies = (this.discussion.replies || 0) + 1;
        }
        this.loadComments();
      })
      .catch(() => {
        // Error already handled by service / toaster
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  toggleCommentUpvote(comment: DiscussionComment): void {
    const upvotes = comment.upvotes ?? 0;
    comment.upvotes = upvotes;
    const wasVoted = comment.userUpvoted;
    if (wasVoted) {
      comment.upvotes = upvotes - 1;
      comment.userUpvoted = false;
    } else {
      comment.upvotes = upvotes + 1;
      comment.userUpvoted = true;
    }
    const apiCall = wasVoted
      ? this.discussionApiService.deleteCommentVote(comment.id)
      : this.discussionApiService.createCommentVote(comment.id);
    apiCall.catch(() => {
      if (wasVoted) {
        comment.upvotes = (comment.upvotes ?? 0) + 1;
        comment.userUpvoted = true;
      } else {
        comment.upvotes = Math.max(0, (comment.upvotes ?? 0) - 1);
        comment.userUpvoted = false;
      }
      this.toasterService.error('Failed to update vote. Please try again.');
    });
  }

  /** Max number of different reactions a user can add on a single comment */
  readonly maxReactionsPerUser = 1;

  toggleReaction(comment: DiscussionComment, reaction: string): void {
    if (!comment.reactions) {
      comment.reactions = {};
    }
    if (!comment.userReactions) {
      comment.userReactions = [];
    }

    const index = comment.userReactions.indexOf(reaction);
    const wasAdding = index === -1;
    let oldReactionForReplace: string | null = null;

    if (wasAdding && comment.userReactions.length >= this.maxReactionsPerUser) {
      // Replace existing reaction with the new one (user can have only one)
      oldReactionForReplace = comment.userReactions[0];
      comment.userReactions.splice(0, 1);
      comment.reactions[oldReactionForReplace] = (comment.reactions[oldReactionForReplace] || 1) - 1;
      if (comment.reactions[oldReactionForReplace] <= 0) {
        delete comment.reactions[oldReactionForReplace];
      }
    }

    if (index > -1) {
      // Remove reaction (optimistic)
      comment.userReactions.splice(index, 1);
      comment.reactions[reaction] = (comment.reactions[reaction] || 1) - 1;
      if (comment.reactions[reaction] <= 0) {
        delete comment.reactions[reaction];
      }
    } else {
      // Add reaction (optimistic)
      comment.userReactions.push(reaction);
      comment.reactions[reaction] = (comment.reactions[reaction] || 0) + 1;
    }

    const emojiCode = this.emojiToDecimalHtmlEntity(reaction);
    this.discussionApiService
      .addCommentReaction(comment.id, emojiCode)
      .catch((err) => {
        if (wasAdding && oldReactionForReplace) {
          comment.userReactions!.splice(comment.userReactions!.indexOf(reaction), 1);
          comment.reactions![reaction] = (comment.reactions![reaction] || 1) - 1;
          if (comment.reactions![reaction] <= 0) delete comment.reactions![reaction];
          comment.userReactions!.unshift(oldReactionForReplace);
          comment.reactions![oldReactionForReplace] = (comment.reactions![oldReactionForReplace] || 0) + 1;
        } else if (wasAdding) {
          const idx = comment.userReactions!.indexOf(reaction);
          if (idx > -1) comment.userReactions!.splice(idx, 1);
          comment.reactions![reaction] = (comment.reactions![reaction] || 1) - 1;
          if (comment.reactions![reaction] <= 0) delete comment.reactions![reaction];
        } else {
          comment.userReactions!.push(reaction);
          comment.reactions![reaction] = (comment.reactions![reaction] || 0) + 1;
        }
        this.toasterService.error('Failed to update reaction. Please try again.');
      });
  }

  /** Convert emoji to decimal HTML entity for API (e.g. 😂 → &#128514;, 👍 → &#128077;) */
  private emojiToDecimalHtmlEntity(emoji: string): string {
    return Array.from(emoji)
      .map((char) => '&#' + (char.codePointAt(0) ?? 0) + ';')
      .join('');
  }

  /** Convert API decimal HTML entity string to emoji character (e.g. "&#10084;&#65039;" → "❤️") */
  private decimalHtmlEntityToEmoji(entityStr: string): string {
    if (!entityStr || typeof entityStr !== 'string') return '';
    return entityStr.replace(/&#(\d+);/g, (_, num) =>
      String.fromCodePoint(parseInt(num, 10))
    );
  }

  hasReaction(comment: DiscussionComment, reaction: string): boolean {
    return comment.userReactions?.includes(reaction) || false;
  }

  /** True if the current user can add this reaction (under the per-user limit, or replace when limit is 1). */
  canAddReaction(comment: DiscussionComment, reaction: string): boolean {
    if (this.hasReaction(comment, reaction)) return true;
    const count = comment.userReactions?.length ?? 0;
    if (count < this.maxReactionsPerUser) return true;
    if (count === 1 && this.maxReactionsPerUser === 1) return true; // allow replace
    return false;
  }

  getReactionCount(comment: DiscussionComment, reaction: string): number {
    return comment.reactions?.[reaction] || 0;
  }

  showReactionPicker: { [commentId: string]: boolean } = {};

  toggleReactionPicker(commentId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showReactionPicker[commentId] = !this.showReactionPicker[commentId];
  }

  availableReactions = ['👍', '❤️', '🔥', '😂', '😮', '😢', '🙏'];

  hasReactions(comment: DiscussionComment): boolean {
    return comment.reactions ? Object.keys(comment.reactions).length > 0 : false;
  }

  /** All emoji keys that have a count on this comment (so API reactions like 🔥 display even if not in picker) */
  getReactionKeys(comment: DiscussionComment): string[] {
    return comment.reactions ? Object.keys(comment.reactions) : [];
  }

  getReplyCount(comment: DiscussionComment): number {
    return comment.replies ? comment.replies.length : 0;
  }

  isRepliesExpanded(commentId: string): boolean {
    return this.repliesExpanded[commentId] !== false;
  }

  toggleReplies(commentId: string): void {
    this.repliesExpanded[commentId] = !this.isRepliesExpanded(commentId);
  }

  isPending(comment: DiscussionComment): boolean {
    return comment.status === 'pending';
  }

  isRejected(comment: DiscussionComment): boolean {
    return comment.status === 'rejected';
  }

  isApproved(comment: DiscussionComment): boolean {
    return comment.status === 'approved' || !comment.status; // Default to approved if status not set
  }

  /** Map API status (e.g. REJECTED, is_approved: false) to CommentStatus. */
  private normalizeCommentStatus(item: any): CommentStatus {
    const raw = item.status?.toLowerCase?.();
    if (raw === 'rejected' || raw === 'pending' || raw === 'approved') return raw as CommentStatus;
    if (item.is_approved === false) return 'rejected';
    return raw ? (raw as CommentStatus) : 'approved';
  }
}
